import axios from 'axios';
import localforage from 'localforage';
import { apiUrlX } from 'src/config';
import store from '../store/index';

const V3_URL = '/v3/';
const V3_HOSTS = ['stenci.app', 'stg.stenci.app'];
const V3_TOKEN_KEY = 'auth_token';
const V3_USER_KEY = 'auth_user';
const V3_LEAVE_TIMEOUT = 1500;
const V2_TOKEN_KEY = 'v2_auth_token';
const V2_TOKEN_MAX_AGE = 60000;

function hostHasV3() {
  try {
    return V3_HOSTS.includes(window.location.hostname);
  } catch (e) {
    return false;
  }
}

function getV3Token() {
  try {
    return localStorage.getItem(V3_TOKEN_KEY) || null;
  } catch (e) {
    return null;
  }
}

function dropV3Session() {
  try {
    localStorage.removeItem(V3_TOKEN_KEY);
    localStorage.removeItem(V3_USER_KEY);
    return true;
  } catch (e) {
    return false;
  }
}

function takeReturnedToken() {
  try {
    const raw = localStorage.getItem(V2_TOKEN_KEY);
    if (!raw) {
      return null;
    }
    localStorage.removeItem(V2_TOKEN_KEY);

    const { token, at } = JSON.parse(raw);
    const age = Date.now() - at;

    if (typeof token !== 'string' || !token || !(age >= 0 && age <= V2_TOKEN_MAX_AGE)) {
      return null;
    }

    return token;
  } catch (e) {
    return null;
  }
}

function forgetV2Token() {
  const cleared = localforage.removeItem('token')
    .then(() => localforage.getItem('token'))
    .then(left => (left ? localforage.removeItem('token') : null))
    .catch(() => null);
  const limit = new Promise((resolve) => {
    setTimeout(resolve, V3_LEAVE_TIMEOUT);
  });

  return Promise.race([cleared, limit]);
}

function openV3() {
  try {
    const { user, token } = store.state.auth;

    if (!token || !user || !user.branch || user.branch.v3Enabled !== true) {
      return false;
    }

    if (user.licenseExceeded === true) {
      return false;
    }

    if (!hostHasV3()) {
      return false;
    }

    localStorage.setItem(V3_TOKEN_KEY, token);
    forgetV2Token().then(() => window.location.replace(V3_URL));
    return true;
  } catch (e) {
    return false;
  }
}

function assume(token) {
  return Promise.all([
    store.dispatch('setToken', token),
    store.dispatch('setModule', 'clinical'),
  ]);
}

function adopt(token) {
  return axios.create({ baseURL: apiUrlX, timeout: 90000 })
    .get('/me', { headers: { Authorization: `JWT ${token}` } })
    .then(({ data }) => assume(token).then(() => store.dispatch('setUser', data)));
}

function checkSession() {
  const token = takeReturnedToken();

  if (!token || store.state.auth.token) {
    return store.dispatch('checkUserToken');
  }

  return adopt(token).catch((error) => {
    if (error && error.response && error.response.status === 401) {
      return store.dispatch('checkUserToken');
    }

    return assume(token).then(() => Promise.reject(new Error('FAIL_IN_LOAD_USER')));
  });
}

function resumeFromV3(next) {
  const token = getV3Token();

  if (!token) {
    next('/login');
    return;
  }

  adopt(token).then(
    () => {
      if (openV3()) {
        return;
      }
      dropV3Session();
      next();
    },
    () => {
      dropV3Session();
      store.dispatch('setToken', null);
      next('/login');
    },
  );
}

export default (to, from, next) => {
  if (to.meta.requiresAuth !== true) {
    next();
    return;
  }

  checkSession()
    .then(() => {
      if (!openV3()) {
        next();
      }
    })
    .catch((error) => {
      if (error && error.message === 'NO_TOKEN' && hostHasV3()) {
        resumeFromV3(next);
        return;
      }
      next('/login');
    });
};
